import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import {
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Modal,
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Toolbar,
  Typography,
  makeStyles,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";

// Stiluri pentru modal
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

const isAutofacturaCompleted = (item = {}) => {
  if (String(item.has_autofactura_attachment ?? "").trim() === "1") {
    return true;
  }

  const status = String(item.autofacturafacuta ?? "")
    .trim()
    .toUpperCase();
  if (["1", "DA", "TRUE", "YES"].includes(status)) {
    return true;
  }

  const attachmentText =
    typeof item.attachments === "string"
      ? item.attachments
      : JSON.stringify(item.attachments || []);
  return /autofactura_[a-z]{1,10}\d{6,12}(?:_[^"\\/]+)?\.pdf/i.test(
    attachmentText,
  );
};

const isAutofacturaHidden = (item = {}) =>
  String(item.hideautofactura ?? "").trim() === "1";

const sanitizeFilenamePart = (value, fallback = "") => {
  const sanitized = String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9.-]+/g, "_")
    .replace(/^[_\s.-]+|[_\s.-]+$/g, "")
    .slice(0, 100);
  return sanitized || fallback;
};

const getDownloadFilename = (contentDisposition, fallback) => {
  const header = String(contentDisposition || "");
  const encodedMatch = header.match(/filename\*=UTF-8''([^;]+)/i);
  const plainMatch = header.match(/filename="?([^";]+)"?/i);
  let filename = encodedMatch?.[1] || plainMatch?.[1] || fallback;

  try {
    filename = decodeURIComponent(filename);
  } catch (_decodeError) {
    // Păstrăm numele primit dacă nu este URL-encoded corect.
  }

  const basename = String(filename || fallback)
    .split(/[\\/]/)
    .pop()
    .replace(/\.pdf$/i, "");
  return `${sanitizeFilenamePart(basename, "Autofactura")}.pdf`;
};

const isPdfBlob = async (blob) => {
  if (!(blob instanceof Blob) || blob.size < 5) {
    return false;
  }
  return (await blob.slice(0, 5).text()) === "%PDF-";
};

const getLocalDateInputValue = () => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 10);
};

const getRequestErrorMessage = async (error) => {
  const payload = error?.response?.data;
  if (typeof Blob !== "undefined" && payload instanceof Blob) {
    const text = await payload.text();
    try {
      const parsed = JSON.parse(text);
      return parsed.message || parsed.error || text || error?.message;
    } catch (_parseError) {
      return text || error?.message;
    }
  }
  if (typeof payload === "string" && payload.trim()) {
    return payload;
  }
  return (
    payload?.message || payload?.error || error?.message || "Eroare necunoscută"
  );
};

const DataDisplay = () => {
  const [data, setData] = useState([]);
  const [emitDate, setEmitDate] = useState(getLocalDateInputValue);
  const [serie, setSerie] = useState("ALEB");
  const [currentNumber, setCurrentNumber] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [invoiceToUpdate, setInvoiceToUpdate] = useState({}); // Datele facturii de actualizat
  const [modalUpdateOpen, setModalUpdateOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [currentInvoiceData, setCurrentInvoiceData] = useState({
    serie: "",
    numar: "",
  });
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1); // Luni de la 1 la 12
  const [availableYears, setAvailableYears] = useState(() => [
    new Date().getFullYear(),
  ]);
  const [availableMonths, setAvailableMonths] = useState(() => [
    new Date().getMonth() + 1,
  ]);
  const [generatingIssueId, setGeneratingIssueId] = useState(null);
  const generationInProgressRef = useRef(false);
  const fetchDataRequestRef = useRef(0);
  const years = Array.from(
    { length: 10 },
    (_, i) => new Date().getFullYear() - i,
  );
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const [currentNumberInput, setCurrentNumberInput] = useState("");

  const fetchData = useCallback(async () => {
    const requestId = ++fetchDataRequestRef.current;
    try {
      const url = `https://server-9p4hx.kinsta.app/api/get-data-autofactura`;
      const response = await axios.get(url);
      if (requestId !== fetchDataRequestRef.current) {
        return;
      }

      let yearsSet = new Set();
      let monthsMap = new Map();

      response.data.forEach((item) => {
        if (!isAutofacturaHidden(item) && !isAutofacturaCompleted(item)) {
          const itemDate = new Date(item.data_relevanta);
          const year = itemDate.getFullYear();
          const month = itemDate.getMonth() + 1; // getMonth returnează luna de la 0 la 11

          yearsSet.add(year);

          if (!monthsMap.has(year)) {
            monthsMap.set(year, new Set());
          }
          monthsMap.get(year).add(month);
        }
      });

      yearsSet.add(filterYear);
      if (!monthsMap.has(filterYear)) {
        monthsMap.set(filterYear, new Set());
      }
      monthsMap.get(filterYear).add(filterMonth);

      const sortedYears = [...yearsSet].sort((a, b) => a - b);

      setAvailableYears(sortedYears);
      // Setează lunile disponibile bazat pe anul selectat
      setAvailableMonths(
        [...(monthsMap.get(filterYear) || [])].sort((a, b) => a - b),
      );

      const filteredData = response.data.filter((item) => {
        if (!item.data_relevanta) return false;

        const [year, month] = item.data_relevanta.split("-").map(Number);
        return (
          year === filterYear &&
          month === filterMonth &&
          !isAutofacturaHidden(item) &&
          !isAutofacturaCompleted(item)
        );
      });

      setData(filteredData);
      return filteredData;
    } catch (error) {
      if (requestId === fetchDataRequestRef.current) {
        console.error("Eroare la preluarea datelor:", error);
      }
      return null;
    }
  }, [filterMonth, filterYear]);

  // useEffect pentru încărcarea datelor la inițializare și la schimbarea anului sau lunii
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const fetchCurrentNumber = async () => {
      try {
        const response = await axios.get(
          "https://server-9p4hx.kinsta.app/get-current-number",
        );
        if (response.data && response.data.numar) {
          setCurrentNumber(response.data.numar);
          // Setează `currentNumberInput` pentru a afișa numărul curent în câmpul de input
          setCurrentNumberInput(response.data.numar);
          if (response.data.serie) {
            setSerie(String(response.data.serie).toUpperCase());
          }
        }
      } catch (error) {
        console.error("Eroare la obținerea numărului curent:", error);
      }
    };

    fetchCurrentNumber();
  }, []);

  const incrementNumber = async (item) => {
    const response = await axios.post(
      "https://server-9p4hx.kinsta.app/increment-number",
      {
        issue_id: item.issue_id,
        serie,
      },
    );
    return response.data;
  };

  const handleGenerateInvoice = async (item) => {
    if (generationInProgressRef.current) {
      return;
    }

    generationInProgressRef.current = true;
    setGeneratingIssueId(item.issue_id);
    try {
      const numberReservation = await incrementNumber(item);
      if (!numberReservation?.numar || !numberReservation?.autofacturaid) {
        throw new Error("Nu am putut obține numărul următor al autofacturii.");
      }

      const autofacturaid = numberReservation.autofacturaid;
      const invoiceDetails = {
        issue_id: item.issue_id,
        emitDate,
        autofacturaid,
      };
      setCurrentNumber(numberReservation.numar);
      setCurrentNumberInput(numberReservation.numar);

      const response = await axios.post(
        "https://server-9p4hx.kinsta.app/api/generate-invoice",
        invoiceDetails,
        {
          responseType: "blob",
        },
      );

      setData((currentData) =>
        currentData.filter((invoice) => invoice.issue_id !== item.issue_id),
      );
      await fetchData();

      let downloadWarning = "";
      try {
        if (!(await isPdfBlob(response.data))) {
          throw new Error("Răspunsul primit nu conține un PDF valid.");
        }

        const fallbackFilename = `Autofactura_${sanitizeFilenamePart(
          autofacturaid,
        )}_${sanitizeFilenamePart(item.issue_id)}.pdf`;
        const filename = getDownloadFilename(
          response.headers?.["content-disposition"],
          fallbackFilename,
        );
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement("a");
        try {
          link.href = url;
          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();
        } finally {
          link.remove();
          window.URL.revokeObjectURL(url);
        }
      } catch (downloadError) {
        console.error(
          "Eroare la descărcarea locală a autofacturii:",
          downloadError,
        );
        downloadWarning =
          " PDF-ul este în Jira, dar descărcarea locală nu a reușit.";
      }

      alert(
        `Autofactura ${autofacturaid} a fost creată și atașată în Jira la ${item.issue_id}.${downloadWarning}`,
      );
    } catch (error) {
      console.error("Eroare la generarea facturii:", error);
      const refreshedData = await fetchData();
      const message = await getRequestErrorMessage(error);
      const listMessage = Array.isArray(refreshedData)
        ? refreshedData.some((invoice) => invoice.issue_id === item.issue_id)
          ? "Cererea a rămas în listă."
          : "Lista a fost actualizată și cererea nu mai este de procesat."
        : "Reîncărcați lista pentru a verifica starea cererii.";
      alert(
        `Autofactura nu a putut fi finalizată în această încercare. ${listMessage}\n${message}`,
      );
    } finally {
      generationInProgressRef.current = false;
      setGeneratingIssueId(null);
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.post(
        "https://server-9p4hx.kinsta.app/update-json-data-autofactura",
      );
      fetchData(); // Reîncarcă datele după actualizare
    } catch (error) {
      console.error("Eroare la actualizarea datelor:", error);
    }
  };

  const fetchCurrentNumber = async () => {
    try {
      const response = await axios.get(
        "https://server-9p4hx.kinsta.app/get-current-number",
      );
      return response.data; // Aici se presupune că răspunsul include atât seria cât și numărul
    } catch (error) {
      console.error("Eroare la obținerea numărului curent:", error);
    }
  };

  const handleSetSerie = async () => {
    try {
      // Trimiterea valorii seriei către backend
      const response = await axios.post(
        "https://server-9p4hx.kinsta.app/set-serie",
        {
          serie: serie,
        },
      );
      // Verifică răspunsul și actualizează starea dacă este necesar
      if (response.status === 200) {
        console.log("Serie actualizată cu succes în backend.");
        // Reîncarcă numărul curent după ce setezi seria, dacă este necesar
        const data = await fetchCurrentNumber();
        if (data) {
          setCurrentInvoiceData(data);
        }
      } else {
        console.error("Nu s-a reușit actualizarea seriei în backend.");
      }
    } catch (error) {
      console.error("Eroare la setarea seriei:", error);
    }
  };

  const openUpdateModal = (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
    setModalUpdateOpen(true);
    // Presupunem că avem o funcție care încarcă datele facturii pentru a fi editate
    loadInvoiceData(invoiceId);
  };

  const closeUpdateModal = () => {
    setModalUpdateOpen(false);
    setSearchTerm("");
    setSearchResults([]);
    setSelectedInvoice(null);
  };

  const handleSearch = async () => {
    // Logica de căutare în baza de date
    console.log("Căutare pentru", searchTerm);

    try {
      // Trimitere cerere GET la server cu termenul de căutare
      const response = await axios.get(
        `https://server-9p4hx.kinsta.app/api/search-invoice`,
        {
          params: { term: searchTerm },
        },
      );

      // Setarea rezultatelor căutării în starea componentei
      setSearchResults(response.data);

      // Opțional: Resetarea termenului de căutare după afișarea rezultatelor
      setSearchTerm("");
    } catch (error) {
      console.error("Eroare la căutarea facturilor:", error);
      // Opțional: Gestionează erorile, cum ar fi afișarea unui mesaj de eroare în UI
    }
  };

  const selectInvoiceForUpdate = (invoice) => {
    setSelectedInvoice(invoice); // Aici setăm factura selectată în starea componentei
    setSearchResults([]); // Curățăm rezultatele căutării pentru a ascunde lista de căutare
    setSearchTerm(""); // Opțional, resetăm termenul de căutare
  };

  const loadInvoiceData = async (invoiceId) => {
    console.log("Încărcare date pentru factura", invoiceId);

    try {
      const response = await axios.get(
        `https://server-9p4hx.kinsta.app/api/get-data-autofactura`,
        {
          params: { id: invoiceId },
        },
      );

      if (response.data) {
        const invoiceData = response.data;

        // Setează datele facturii în starea componentei pentru a le putea edita
        // De exemplu: setInvoiceToUpdate(invoiceData);
      } else {
        console.log("Nu s-au găsit date pentru factura cu ID-ul:", invoiceId);
        // Gestionarea cazului în care factura nu este găsită
      }
    } catch (error) {
      console.error("Eroare la încărcarea datelor facturii:", error);
      // Gestionarea erorilor
    }
  };

  const updateInvoiceData = async (invoiceData) => {
    try {
      // Presupunând că endpoint-ul și metoda sunt corecte
      const response = await axios.post(
        "https://server-9p4hx.kinsta.app/api/update-invoice",
        invoiceData,
      );

      if (response.status === 200) {
        console.log("Factura a fost actualizată cu succes.");
        // Alte acțiuni după actualizare, cum ar fi reîncărcarea datelor sau închiderea modalului
      } else {
        console.log("A apărut o problemă la actualizarea facturii.");
      }
    } catch (error) {
      console.error("Eroare la actualizarea facturii:", error);
      // Gestionarea erorilor, de exemplu afișarea unui mesaj de eroare
    }
  };

  const downloadInvoice = async (invoiceId) => {
    try {
      const response = await axios({
        url: `https://server-9p4hx.kinsta.app/api/download-invoice/${invoiceId}`,
        method: "GET",
        responseType: "blob", // Important pentru a primi un fișier
      });

      // Crearea unui URL pentru descărcare
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      // Setarea atributelor pentru descărcare, incluzând numele fișierului
      link.setAttribute("download", `factura-${invoiceId}.pdf`); // Presupunând că factura este un PDF
      document.body.appendChild(link);

      // Declanșarea descărcării și curățarea ulterioară
      link.click();
      window.URL.revokeObjectURL(url);
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error("Eroare la descărcarea facturii:", error);
      // Gestionarea erorilor
    }
  };

  const handleHideInvoice = async (id) => {
    try {
      await axios.post("https://server-9p4hx.kinsta.app/api/hide-invoice", {
        id,
      });
      // Reîncărcarea datelor pentru a reflecta schimbările
      fetchData();
    } catch (error) {
      console.error("Eroare la ascunderea facturii:", error);
    }
  };

  const handleSetCurrentNumber = async () => {
    const normalizedNumber = currentNumberInput.trim();
    if (!/^\d{6,12}$/.test(normalizedNumber)) {
      alert("Numărul introdus trebuie să conțină între 6 și 12 cifre.");
      return; // Împiedică continuarea execuției dacă condiția nu este îndeplinită
    }

    try {
      // Aici "currentNumberInput" este starea locală care păstrează valoarea introdusă de utilizator
      await axios.post("https://server-9p4hx.kinsta.app/set-current-number", {
        currentNumber: normalizedNumber,
      });
      alert("Numărul curent a fost setat cu succes.");
      setCurrentNumberInput(""); // Opțional: Resetarea câmpului după trimitere
    } catch (error) {
      console.error("Eroare la setarea numărului curent:", error);
      alert("Eroare la setarea numărului curent.");
    }
  };

  const getInvoiceEmitDateType = (item) => {
    if (item.tip_achizitie === "Consignatie" || item.vandut_llp === "DA") {
      return "data_vandut";
    } else if (item.tip_achizitie === "Vanzare Directa") {
      return "data_de_intrare";
    }
    return null;
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <TextField
          label="Data emitere"
          type="date"
          value={emitDate}
          onChange={(e) => setEmitDate(e.target.value)}
          disabled={Boolean(generatingIssueId)}
          InputLabelProps={{ shrink: true }}
          sx={{ mr: 2 }}
        />
        <TextField
          label="Serie"
          value={serie}
          onChange={(e) => setSerie(e.target.value)}
          disabled={Boolean(generatingIssueId)}
          placeholder="Introduceți seria"
          sx={{ mr: 2 }}
        />

        <Button
          variant="contained"
          onClick={handleSetSerie}
          disabled={Boolean(generatingIssueId)}
          sx={{ mr: 1 }}
        >
          Setează Seria
        </Button>
        <TextField
          label="Număr curent"
          value={currentNumberInput}
          onChange={(e) => setCurrentNumberInput(e.target.value)}
          disabled={Boolean(generatingIssueId)}
          placeholder="Introduceți numărul curent"
          sx={{ mr: 2 }}
        />
        <Button
          variant="contained"
          onClick={handleSetCurrentNumber}
          disabled={Boolean(generatingIssueId)}
        >
          Setează Numărul Curent
        </Button>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", ml: 0 }}>
        <FormControl sx={{ m: 1, minWidth: 120 }}>
          <InputLabel id="year-select-label">An</InputLabel>
          <Select
            labelId="year-select-label"
            id="year-select"
            value={filterYear}
            label="An"
            onChange={(e) => setFilterYear(e.target.value)}
            disabled={Boolean(generatingIssueId)}
          >
            {availableYears.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ m: 1, minWidth: 120 }}>
          <InputLabel id="month-select-label">Lună</InputLabel>
          <Select
            labelId="month-select-label"
            id="month-select"
            value={filterMonth}
            label="Lună"
            onChange={(e) => setFilterMonth(e.target.value)}
            disabled={Boolean(generatingIssueId)}
          >
            {availableMonths.map((month) => (
              <MenuItem key={month} value={month}>
                {new Date(0, month - 1).toLocaleString("default", {
                  month: "long",
                })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table sx={{ minWidth: 650 }} aria-label="facturi table">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Tip Contract</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Data relevanta (calculata)</TableCell>
              <TableCell>Data Intrare (originală)</TableCell>
              <TableCell>Data Vandut (originală)</TableCell>
              <TableCell>Vandut LLP</TableCell>
              <TableCell>Nume Prenume</TableCell>
              <TableCell>Telefon</TableCell>
              <TableCell>Summary</TableCell>
              <TableCell>Pret Intrare RON</TableCell>
              <TableCell>Actiuni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item) => (
              <TableRow key={item.issue_id}>
                <TableCell>{item.issue_id}</TableCell>
                <TableCell>{item.tip_achizitie}</TableCell>
                <TableCell>{item.email}</TableCell>
                <TableCell>
                  {" "}
                  {item.data_relevanta
                    ? new Date(item.data_relevanta).toLocaleDateString(
                        "ro-RO",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        },
                      )
                    : "-"}
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor:
                      getInvoiceEmitDateType(item) === "data_de_intrare"
                        ? "#e0f7fa"
                        : "inherit",
                  }}
                >
                  {item.data_de_intrare
                    ? new Date(item.data_de_intrare).toLocaleDateString(
                        "ro-RO",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        },
                      )
                    : "-"}
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor:
                      getInvoiceEmitDateType(item) === "data_vandut"
                        ? "#e0f7fa"
                        : "inherit",
                  }}
                >
                  {item.data_vandut
                    ? new Date(item.data_vandut).toLocaleDateString("ro-RO", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                    : "-"}
                </TableCell>
                <TableCell>{item.vandut_llp || "-"}</TableCell>
                <TableCell>{item.nume_si_prenume}</TableCell>
                <TableCell>{item.telefon}</TableCell>
                <TableCell>{item.summary}</TableCell>
                <TableCell>{item.pret_intrare_ron}</TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => handleGenerateInvoice(item)}
                    disabled={Boolean(generatingIssueId)}
                    title={
                      generatingIssueId === item.issue_id
                        ? "Se generează și se atașează în Jira"
                        : "Generează autofactura"
                    }
                  >
                    {generatingIssueId === item.issue_id ? (
                      <CircularProgress size={22} />
                    ) : (
                      <PrintIcon />
                    )}
                  </IconButton>
                  <IconButton
                    color="secondary"
                    onClick={() => handleHideInvoice(item.issue_id)}
                    disabled={Boolean(generatingIssueId)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DataDisplay;
